import React from 'react';
import { motion } from 'framer-motion';

const TeacherSlide06ZPD: React.FC = () => {
  const levels = [
    { level: 1, name: 'Very Easy', xp: '10 XP', color: '#22c55e' },
    { level: 2, name: 'Easy', xp: '15 XP', color: '#84cc16' },
    { level: 3, name: 'Medium', xp: '20 XP', color: '#eab308' },
    { level: 4, name: 'Hard', xp: '25 XP', color: '#f97316' },
    { level: 5, name: 'Very Hard', xp: '30 XP', color: '#ef4444' },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-3 text-center"
      >
        Zone of Proximal Development
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-xl text-[#94a3b8] mb-10 text-center max-w-4xl"
      >
        Adaptive difficulty keeps students in the "sweet spot" — challenging enough to grow, but not so hard they give up
      </motion.p>

      <div className="flex gap-10 w-full max-w-6xl">
        {/* Left: ZPD Zones */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex-1"
        >
          <div className="flex items-stretch gap-4 h-48">
            {/* Too Easy */}
            <div
              className="flex-1 rounded-xl p-5 flex flex-col items-center justify-center text-center"
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '2px solid rgba(239, 68, 68, 0.4)',
              }}
            >
              <div className="text-4xl mb-2">😴</div>
              <div className="text-lg font-semibold text-[#ef4444]">Too Easy</div>
              <div className="text-sm text-[#94a3b8] mt-1">Boredom</div>
              <div className="text-xs text-[#64748b] mt-2">No growth</div>
            </div>

            {/* Sweet Spot */}
            <div
              className="flex-[1.5] rounded-xl p-5 flex flex-col items-center justify-center text-center relative"
              style={{
                background: 'rgba(16, 185, 129, 0.2)',
                border: '3px solid #10b981',
                boxShadow: '0 0 40px rgba(16, 185, 129, 0.3)',
              }}
            >
              <div className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold bg-[#10b981] text-white">
                TARGET
              </div>
              <div className="text-4xl mb-2">🎯</div>
              <div className="text-xl font-semibold text-[#10b981]">ZPD</div>
              <div className="text-sm text-white mt-1">Optimal Challenge</div>
              <div className="text-xs text-[#94a3b8] mt-2">Maximum learning</div>
            </div>

            {/* Too Hard */}
            <div
              className="flex-1 rounded-xl p-5 flex flex-col items-center justify-center text-center"
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '2px solid rgba(239, 68, 68, 0.4)',
              }}
            >
              <div className="text-4xl mb-2">😰</div>
              <div className="text-lg font-semibold text-[#ef4444]">Too Hard</div>
              <div className="text-sm text-[#94a3b8] mt-1">Frustration</div>
              <div className="text-xs text-[#64748b] mt-2">Give up</div>
            </div>
          </div>

          <div className="flex justify-center gap-10 mt-8">
            <div className="text-center">
              <div className="text-2xl mb-2">📈</div>
              <div className="text-white font-medium">Doing well?</div>
              <div className="text-sm text-[#94a3b8]">Increase difficulty</div>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-2">📉</div>
              <div className="text-white font-medium">Struggling?</div>
              <div className="text-sm text-[#94a3b8]">Decrease & show hints</div>
            </div>
          </div>
        </motion.div>

        {/* Right: 5 Difficulty Levels */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="w-72"
        >
          <h3 className="text-xl font-semibold text-white mb-4">5 Difficulty Levels</h3>

          <div className="space-y-2">
            {levels.map((item) => (
              <div
                key={item.level}
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{
                  background: `${item.color}15`,
                  border: `1px solid ${item.color}50`,
                }}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                  style={{ background: item.color }}
                >
                  {item.level}
                </div>
                <div className="flex-1">
                  <div className="text-white font-medium">{item.name}</div>
                </div>
                <div className="text-sm font-medium" style={{ color: item.color }}>
                  {item.xp}
                </div>
              </div>
            ))}
          </div>

          <div
            className="mt-4 p-3 rounded-lg text-sm text-[#94a3b8]"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <span className="text-white font-medium">Key insight:</span> It's not about how many questions — it's about answering at the right difficulty level.
          </div>
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="mt-8 text-lg text-[#64748b] text-center italic"
      >
        "I'd rather my kids do 20-30 minutes of focused homework than waste hours on concepts they already understand."
      </motion.p>
    </div>
  );
};

export default TeacherSlide06ZPD;
